import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Store, Mail, MailCheck, TrendingUp, RefreshCw, MailX, Activity, Play, Zap, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { adminFetch } from "./adminUtils";
import UxInsightsCard from "../../components/admin/UxInsightsCard";

/** Helper seguro para fetch del panel admin — evita "body stream already read" */
// Nota: adminFetch también está en adminUtils.jsx — aquí lo usamos importado

// ─── Stat card clickable ──────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, accent, testId, loading, badge, to }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={testId}
      onClick={() => to && navigate(to)}
      className={`bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center gap-4
        ${to ? "cursor-pointer hover:border-slate-600 hover:bg-slate-800/50 transition-colors group" : ""}`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
          {badge && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400
                             bg-emerald-500/10 border border-emerald-500/20 rounded-full px-1.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              EN VIVO
            </span>
          )}
        </div>
        {loading ? (
          <div className="h-7 w-16 bg-slate-800 rounded animate-pulse mt-1" />
        ) : (
          <p className="text-white text-2xl font-bold mt-0.5">{value ?? "—"}</p>
        )}
        {to && !loading && (
          <p className="text-muted-foreground text-[10px] mt-0.5 group-hover:text-orange-400 transition-colors">
            Ver detalle →
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Custom tooltip ───────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl text-xs">
      <p className="text-slate-400 mb-2 font-semibold">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Event Bus operational widget ─────────────────────────────────────
function formatLag(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function EventBusWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    try {
      const data = await adminFetch("/api/admin/event-bus/stats");
      setStats(data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 10_000);
    return () => clearInterval(iv);
  }, []);

  const pending = stats?.by_status?.pending?.count || 0;
  const processing = stats?.by_status?.processing?.count || 0;
  const done = stats?.by_status?.done?.count || 0;
  const dead = stats?.by_status?.dead?.count || 0;
  const lag = stats?.pending_lag_seconds;
  const backend = stats?.backend || "—";
  const isStale = lag != null && lag > 60;

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4"
      data-testid="admin-event-bus-widget"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-500/15 text-violet-400 flex items-center justify-center">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-slate-200 font-semibold text-sm">Event Bus</h2>
            <p className="text-muted-foreground text-[11px]">
              Backend: <span className="text-slate-400">{backend}</span>
              {dead > 0 && <span className="text-rose-400 ml-2">· {dead} en dead-letter</span>}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="admin-event-bus-refresh"
          className="text-muted-foreground hover:text-slate-300 p-1.5 rounded hover:bg-slate-800"
          title="Refrescar ahora"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error ? (
        <p className="text-rose-400 text-xs">Error al cargar stats. Reintenta en 10s.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="event-bus-kpis">
          {/* Pending */}
          <div className={`rounded-lg p-3 border ${
            pending > 0 ? "bg-amber-500/5 border-amber-500/30" : "bg-slate-800/50 border-slate-700/50"
          }`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <Clock className="w-3 h-3" /> Pending
            </div>
            <p className="text-white text-xl font-bold" data-testid="event-bus-pending">{pending + processing}</p>
          </div>
          {/* Done */}
          <div className="rounded-lg p-3 border bg-emerald-500/5 border-emerald-500/20">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <CheckCircle2 className="w-3 h-3" /> Procesados
            </div>
            <p className="text-white text-xl font-bold" data-testid="event-bus-done">{done.toLocaleString()}</p>
          </div>
          {/* Dead */}
          <div className={`rounded-lg p-3 border ${
            dead > 0 ? "bg-rose-500/10 border-rose-500/40" : "bg-slate-800/50 border-slate-700/50"
          }`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <AlertTriangle className="w-3 h-3" /> Dead-letter
            </div>
            <p className={`text-xl font-bold ${dead > 0 ? "text-rose-400" : "text-white"}`} data-testid="event-bus-dead">{dead}</p>
          </div>
          {/* Lag */}
          <div className={`rounded-lg p-3 border ${
            isStale ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-800/50 border-slate-700/50"
          }`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <Activity className="w-3 h-3" /> Lag
            </div>
            <p className={`text-xl font-bold ${isStale ? "text-amber-400" : "text-white"}`} data-testid="event-bus-lag">{formatLag(lag)}</p>
          </div>
        </div>
      )}

      {/* Top event types */}
      {!error && stats?.top_event_types?.length > 0 && (
        <div className="border-t border-slate-800 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Top eventos (últimos 1000)</p>
          <div className="flex flex-wrap gap-1.5">
            {stats.top_event_types.slice(0, 6).map((t) => (
              <span
                key={t.event_type}
                className="text-[11px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full border border-slate-700"
              >
                {t.event_type} <span className="text-muted-foreground">· {t.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [period, setPeriod] = useState("weekly");
  const [demoEnabled, setDemoEnabled] = useState(true);
  const [togglingDemo, setTogglingDemo] = useState(false);

  const fetchedRef = useRef(false);

  const loadSiteSettings = async () => {
    try {
      const data = await adminFetch("/api/site-settings");
      setDemoEnabled(data.demo_enabled !== false);
    } catch { /* ignore */ }
  };

  const toggleDemo = async () => {
    setTogglingDemo(true);
    try {
      const data = await adminFetch("/api/admin/site-settings", {
        method: "PUT",
        body: JSON.stringify({ demo_enabled: !demoEnabled }),
      });
      setDemoEnabled(data.demo_enabled !== false);
      toast.success(data.demo_enabled ? "Demo interactiva activada" : "Demo interactiva desactivada");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingDemo(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const data = await adminFetch("/api/admin/stats");
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadTimeline = async (p = period) => {
    setLoadingChart(true);
    try {
      const data = await adminFetch(`/api/admin/stats/timeline?period=${p}`);
      setTimeline(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingChart(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadStats();
    loadTimeline();
    loadSiteSettings();
  }, []); // eslint-disable-line

  const handlePeriodChange = (p) => {
    setPeriod(p);
    loadTimeline(p);
  };

  const handleRefresh = () => { loadStats(); loadTimeline(period); };

  // Check if there's any chart data worth showing
  const hasChartData = timeline.some(
    (d) => d.invitations_sent > 0 || d.invitations_accepted > 0 || d.users_registered > 0
  );

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-white text-2xl font-bold">Panel de Control</h1>
          <p className="text-muted-foreground text-sm mt-1">Estadísticas globales de la plataforma</p>
        </div>
        <button
          onClick={handleRefresh}
          data-testid="admin-refresh-stats"
          className="flex items-center gap-2 text-muted-foreground hover:text-slate-300 text-sm
                     transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-800"
        >
          <RefreshCw className={`w-4 h-4 ${(loadingStats || loadingChart) ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {/* Event Bus operational widget */}
      <EventBusWidget />

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Users}
          label="Usuarios registrados"
          value={stats?.total_users}
          accent="bg-blue-500/15 text-blue-400"
          testId="stat-total-users"
          loading={loadingStats}
          to="/admin/users"
        />
        <StatCard
          icon={Activity}
          label="Usuarios con sesión activa"
          value={stats?.active_users}
          accent="bg-emerald-500/15 text-emerald-400"
          testId="stat-active-users"
          loading={loadingStats}
          badge
          to="/admin/users"
        />
        <StatCard
          icon={Store}
          label="Restaurantes creados"
          value={stats?.total_restaurants}
          accent="bg-indigo-500/15 text-indigo-400"
          testId="stat-total-restaurants"
          loading={loadingStats}
        />
        <StatCard
          icon={Mail}
          label="Invitaciones enviadas"
          value={stats?.total_invitations}
          accent="bg-orange-500/15 text-orange-400"
          testId="stat-total-invitations"
          loading={loadingStats}
          to="/admin/invitations"
        />
        <StatCard
          icon={TrendingUp}
          label="Invitaciones pendientes"
          value={stats?.pending_invitations}
          accent="bg-amber-500/15 text-amber-400"
          testId="stat-pending-invitations"
          loading={loadingStats}
          to="/admin/invitations?status=pending"
        />
        <StatCard
          icon={MailCheck}
          label="Invitaciones aceptadas"
          value={stats?.accepted_invitations}
          accent="bg-green-500/15 text-green-400"
          testId="stat-accepted-invitations"
          loading={loadingStats}
          to="/admin/invitations?status=accepted"
        />
      </div>

      {/* Conversion chart */}
      <div
        className="bg-slate-900 border border-slate-800 rounded-xl p-6"
        data-testid="conversion-chart-section"
      >
        {/* Chart header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-slate-200 font-semibold">Evolución de adopción</h2>
            <p className="text-muted-foreground text-xs mt-0.5">
              Invitaciones enviadas vs aceptadas y nuevos usuarios
            </p>
          </div>
          {/* Period toggle */}
          <div className="flex bg-slate-800 rounded-lg p-0.5 gap-0.5">
            {["weekly", "monthly"].map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                data-testid={`period-${p}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  period === p
                    ? "bg-slate-700 text-white"
                    : "text-muted-foreground hover:text-slate-300"
                }`}
              >
                {p === "weekly" ? "Semanal" : "Mensual"}
              </button>
            ))}
          </div>
        </div>

        {loadingChart ? (
          <div className="h-52 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !hasChartData ? (
          <div className="h-52 flex flex-col items-center justify-center gap-2">
            <TrendingUp className="w-10 h-10 text-foreground" />
            <p className="text-muted-foreground text-sm">Sin datos suficientes todavía</p>
            <p className="text-foreground text-xs">
              Los datos aparecerán conforme se envíen y acepten invitaciones
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={timeline} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F97316" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradAccepted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748B", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: "#64748B", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "12px", fontSize: "11px", color: "#94A3B8" }}
              />
              <Area
                type="monotone"
                dataKey="invitations_sent"
                name="Inv. enviadas"
                stroke="#F97316"
                strokeWidth={2}
                fill="url(#gradSent)"
                dot={{ r: 3, fill: "#F97316" }}
                activeDot={{ r: 5 }}
              />
              <Area
                type="monotone"
                dataKey="invitations_accepted"
                name="Inv. aceptadas"
                stroke="#22C55E"
                strokeWidth={2}
                fill="url(#gradAccepted)"
                dot={{ r: 3, fill: "#22C55E" }}
                activeDot={{ r: 5 }}
              />
              <Area
                type="monotone"
                dataKey="users_registered"
                name="Usuarios nuevos"
                stroke="#60A5FA"
                strokeWidth={2}
                fill="url(#gradUsers)"
                dot={{ r: 3, fill: "#60A5FA" }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Funnel / conversion bar chart (only when we have data) */}
      {hasChartData && (
        <div
          className="bg-slate-900 border border-slate-800 rounded-xl p-6"
          data-testid="funnel-chart-section"
        >
          <div className="mb-6">
            <h2 className="text-slate-200 font-semibold">Embudo de conversión</h2>
            <p className="text-muted-foreground text-xs mt-0.5">
              Invitaciones enviadas vs aceptadas por período
            </p>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={timeline} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748B", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: "#64748B", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "12px", fontSize: "11px", color: "#94A3B8" }}
              />
              <Bar
                dataKey="invitations_sent"
                name="Enviadas"
                fill="#F97316"
                opacity={0.85}
                radius={[3, 3, 0, 0]}
              />
              <Bar
                dataKey="invitations_accepted"
                name="Aceptadas"
                fill="#22C55E"
                opacity={0.85}
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Fase 12 · UX Insights */}
      <UxInsightsCard />

      {/* Feature Flags */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="admin-feature-flags">
        <h2 className="text-slate-200 font-semibold text-sm mb-4">Configuracion de la plataforma</h2>
        <div className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${demoEnabled ? "bg-orange-500/15 text-orange-400" : "bg-slate-800 text-muted-foreground"}`}>
              <Play className="w-4 h-4" />
            </div>
            <div>
              <p className="text-slate-300 text-sm font-medium">Demo interactiva</p>
              <p className="text-muted-foreground text-xs">Mostrar boton de demo en la landing page y permitir acceso a /demo</p>
            </div>
          </div>
          <button
            onClick={toggleDemo}
            disabled={togglingDemo}
            data-testid="admin-toggle-demo"
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
              demoEnabled ? "bg-orange-500" : "bg-slate-700"
            } ${togglingDemo ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-card shadow transition-transform duration-200 ${
              demoEnabled ? "translate-x-5" : "translate-x-0"
            }`} />
          </button>
        </div>
      </div>

      {/* Info note */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h2 className="text-slate-300 font-semibold text-sm mb-2">Acceso por invitación</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          MenuGenius es una plataforma de acceso cerrado. Solo los usuarios con una invitación activa
          pueden registrarse. Gestiona las invitaciones desde la sección{" "}
          <span className="text-orange-400 font-medium">Invitaciones</span>.
        </p>
      </div>
    </div>
  );
}
